import {
  Control,
  Controller,
  FieldErrors,
  Input,
  TextEditor,
  Typography,
  UseFormWatch,
} from '@otp/ui';
import { UserMergeRequestForm as Form } from '../types';
import { useTranslation } from 'react-i18next';
import { UsersServerSideField } from 'src/components/common/form/fields';
import { ReasonField } from './fields/reason';
import { UserMergeRequestReason } from 'src/graphql/generated/graphql';
import { StoresField } from './fields/stores';
import { EidsField } from './fields/eids';
import { useUsers } from 'src/hooks/query/use-users';
import { UsernameEmail } from 'src/components/manage-otps/list/table/columns/username-email';

type UserMergeRequestFormProps = {
  control: Control<Form, any, Form>;
  watch: UseFormWatch<Form>;
  errors: FieldErrors<Form>;
  hiddenFields?: string[];
  displayEidsDetails?: boolean;
};

export const UserMergeRequestForm = ({
  control,
  watch,
  errors,
  hiddenFields = [],
  displayEidsDetails = false,
}: UserMergeRequestFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'user-merge-requests.form',
  });

  const reason = watch('reason');
  const eidsToMerge = watch('eIdsToMerge');

  const { users } = useUsers({
    filter: {
      mcIds: eidsToMerge,
    },
    enabled: !!eidsToMerge?.length && displayEidsDetails,
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        {!hiddenFields.includes('userId') && (
          <div className="mb-4">
            <Controller
              name="userId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.eIdsToMerge ? 'red' : ''}
                  >
                    {t('fields.user-id')}
                    <span className="text-red-500"> *</span>
                  </Typography>

                  <UsersServerSideField
                    field={field}
                    filterGenerator={(value) => {
                      return {
                        filter: {
                          userIds: [value],
                        },
                        pagination: {
                          offset: 0,
                          limit: 1,
                        },
                      };
                    }}
                  />
                </div>
              )}
            />
          </div>
        )}
        <div className="mb-4">
          <Controller
            name="eIdsToMerge"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div>
                <Typography
                  className="mb-2"
                  variant="subtitle1"
                  color={!!errors.eIdsToMerge ? 'red' : ''}
                >
                  {t('fields.eids-to-merge')}
                  <span className="text-red-500"> *</span>
                </Typography>

                <EidsField field={field} />

                {displayEidsDetails && (
                  <div>
                    {users?.map((user) => (
                      <UsernameEmail
                        user={user}
                        className="mt-2"
                        options={{
                          withLevel: true,
                          withOperatorName: true,
                          withPrograms: true,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            name="previousStoreNumbers"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div>
                <Typography
                  className="mb-2"
                  variant="subtitle1"
                  color={!!errors.previousStoreNumbers ? 'red' : ''}
                >
                  {t('fields.previous-store-numbers')}
                  <span className="text-red-500"> *</span>
                </Typography>
                <StoresField field={field} />
              </div>
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            name="previousEmail"
            control={control}
            render={({ field }) => (
              <Input
                label={t('fields.previous-email')}
                placeholder={t('fields.previous-email')}
                {...field}
                defaultValue={field.value}
              />
            )}
          />
        </div>
      </div>
      <div>
        <div className="mb-4">
          <Controller
            name="reason"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div>
                <Typography
                  className="mb-2"
                  variant="subtitle1"
                  color={!!errors.reason ? 'red' : ''}
                >
                  {t('fields.reason.label')}
                  <span className="text-red-500"> *</span>
                </Typography>
                <ReasonField field={field} className="mb-4" />
              </div>
            )}
          />
        </div>
        {reason === UserMergeRequestReason.Other && (
          <div className="mb-4">
            <Controller
              name="requestNotes"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.requestNotes ? 'red' : ''}
                  >
                    {t('fields.additional-details')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
        )}
        {!hiddenFields.includes('responseNotes') && (
          <div>
            <Controller
              name="responseNotes"
              control={control}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('fields.response-notes')}
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};
